import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';
import { jsPDF } from 'jspdf';
import autoTable from 'jspdf-autotable';
import { storageService } from '../../../utils/localFileService';
import statisticsService from '../../../services/statisticsService';

const OrderSummary = ({ 
  quantities, 
  products, 
  onQuantityChange, 
  onExportOrder, 
  onPrintOrder, 
  showToast,
  productFlags = {},
  onProductFlagChange
}) => {
  const [isUploadingPDF, setIsUploadingPDF] = useState(false);

  const orderedItems = Object.entries(quantities)?.filter(([sku, qty]) => qty > 0)?.map(([sku, qty]) => {
      const product = products?.find(p => p?.sku === sku);
      return { 
        sku, 
        quantity: qty, 
        name: product?.name || 'Prodotto non trovato',
        price: product?.resellerPrice || product?.price || '0.00',
        resellerPrice: product?.resellerPrice || '0.00',
        publicPrice: product?.publicPrice || product?.price || '0.00',
        category: product?.category || '',
        isFlagged: productFlags?.[sku] || false
      };
    });

  // ✅ Separate flagged and non-flagged products
  const nonFlaggedItems = orderedItems?.filter(item => !item?.isFlagged);
  const flaggedItems = orderedItems?.filter(item => item?.isFlagged);

  const totalItems = orderedItems?.reduce((sum, item) => sum + item?.quantity, 0);
  
  // ✅ Calculate totals with tax for flagged items
  const nonFlaggedTotal = nonFlaggedItems?.reduce((sum, item) => {
    const price = parseFloat(item?.resellerPrice || item?.price || 0);
    const quantity = parseInt(item?.quantity || 0);
    return sum + (price * quantity);
  }, 0);

  const flaggedTotal = flaggedItems?.reduce((sum, item) => {
    const price = parseFloat(item?.resellerPrice || item?.price || 0);
    const quantity = parseInt(item?.quantity || 0);
    return sum + (price * quantity);
  }, 0);

  const flaggedTotalWithTax = flaggedTotal * 1.22; // 22% tax
  const totalAmount = nonFlaggedTotal + flaggedTotalWithTax;

  // Handle direct quantity change in cart
  const handleCartQuantityChange = (sku, newQuantity) => {
    const quantity = Math.max(0, parseInt(newQuantity) || 0);
    onQuantityChange?.(sku, quantity);
  };

  // Handle item deletion from cart
  const handleRemoveItem = (sku) => {
    onQuantityChange?.(sku, 0);
  };

  // Handle flag change in cart
  const handleCartFlagChange = (sku, isChecked) => {
    onProductFlagChange?.(sku, isChecked);
  };

  // ✅ SIMPLIFIED PDF generation with natural content flow - no complex single-page fitting
  const generateSeparatedPDF = () => {
    const pdf = new jsPDF();
    const currentDate = new Date()?.toLocaleDateString('it-IT');
    let currentY = 30;
    const pageHeight = pdf?.internal?.pageSize?.height;
    const marginBottom = 30;
    const maxUsableHeight = pageHeight - marginBottom;

    // Header - always starts the document
    pdf?.setFontSize(18);
    pdf?.text('Ordine', 20, currentY);
    pdf?.setFontSize(12);
    currentY += 15;
    pdf?.text(`Data: ${currentDate}`, 20, currentY);
    currentY += 25; // More space after header

    // ✅ NATURAL FLOW: NON-FLAGGED PRODUCTS SECTION
    if (nonFlaggedItems?.length > 0) {
      // Check if we need space for section header + at least one row
      const minRequiredSpace = 40; // Space for header + first row
      if (currentY + minRequiredSpace > maxUsableHeight) {
        pdf?.addPage();
        currentY = 30;
      }
      
      pdf?.setFontSize(14);
      pdf?.text('PRODOTTI STANDARD', 20, currentY);
      currentY += 15;

      const nonFlaggedTableData = nonFlaggedItems?.map(item => {
        const lineTotal = (parseFloat(item?.resellerPrice || item?.price || 0) * parseInt(item?.quantity))?.toFixed(2);
        return [
          item?.sku,
          item?.name,
          item?.quantity?.toString(),
          `€${(parseFloat(item?.resellerPrice || item?.price || 0))?.toFixed(2)}`,
          `€${lineTotal}`
        ];
      });

      // Generate table with natural page breaks - let jsPDF handle overflow
      autoTable(pdf, {
        startY: currentY,
        head: [['Codice', 'Nome prodotto', 'Quantità', 'Prezzo unitario', 'Totale riga']],
        body: nonFlaggedTableData,
        theme: 'striped',
        headStyles: { fillColor: [41, 128, 185] },
        styles: { fontSize: 10 },
        columnStyles: {
          0: { cellWidth: 25 }, // SKU
          1: { cellWidth: 70 }, // Name  
          2: { cellWidth: 20 }, // Quantity
          3: { cellWidth: 25 }, // Unit price
          4: { cellWidth: 25 }  // Total
        },
        margin: { left: 20, right: 20 },
        showHead: 'everyPage', // Show header on every page if table spans pages
        pageBreak: 'auto' // Let jsPDF handle natural page breaks
      });

      currentY = pdf?.lastAutoTable?.finalY || currentY;
      currentY += 10; // Small gap
      
      // Subtotal - check if there's space, if not move to next page
      const subtotalSpace = 25;
      if (currentY + subtotalSpace > maxUsableHeight) {
        pdf?.addPage();
        currentY = 30;
      }
      
      pdf?.setFontSize(12);
      pdf?.text(`Subtotale prodotti standard: €${nonFlaggedTotal?.toFixed(2)}`, 20, currentY);
      currentY += 20;
    }

    // ✅ NATURAL FLOW: FLAGGED PRODUCTS SECTION
    if (flaggedItems?.length > 0) {
      // Check if we have space for section header + at least one row
      const minRequiredSpace = 40;
      if (currentY + minRequiredSpace > maxUsableHeight) {
        pdf?.addPage();
        currentY = 30;
      }
      
      pdf?.setFontSize(14);
      pdf?.text('PRODOTTI CON IVA 22%', 20, currentY);
      currentY += 15;

      const flaggedTableData = flaggedItems?.map(item => {
        const unitPrice = parseFloat(item?.resellerPrice || item?.price || 0);
        const unitPriceWithTax = unitPrice * 1.22;
        const lineTotal = unitPrice * parseInt(item?.quantity);
        const lineTotalWithTax = lineTotal * 1.22;
        
        return [
          item?.sku,
          item?.name,
          item?.quantity?.toString(),
          `€${unitPrice?.toFixed(2)}`,
          `€${unitPriceWithTax?.toFixed(2)}`,
          `€${lineTotal?.toFixed(2)}`,
          `€${lineTotalWithTax?.toFixed(2)}`
        ];
      });

      // Generate flagged products table with natural page breaks
      autoTable(pdf, {
        startY: currentY,
        head: [['Codice', 'Nome prodotto', 'Qtà', 'Prezzo base', 'Prezzo + IVA', 'Tot. base', 'Tot. + IVA']],
        body: flaggedTableData,
        theme: 'striped',
        headStyles: { fillColor: [220, 53, 69] },
        styles: { fontSize: 9 },
        columnStyles: {
          0: { cellWidth: 20 }, // SKU
          1: { cellWidth: 50 }, // Name
          2: { cellWidth: 15 }, // Quantity
          3: { cellWidth: 22 }, // Base price
          4: { cellWidth: 22 }, // Price with tax
          5: { cellWidth: 22 }, // Base total
          6: { cellWidth: 24 }  // Total with tax
        },
        margin: { left: 20, right: 20 },
        showHead: 'everyPage', // Show header on every page if table spans pages
        pageBreak: 'auto' // Let jsPDF handle natural page breaks
      });

      currentY = pdf?.lastAutoTable?.finalY || currentY;
      currentY += 10; // Small gap
      
      // Flagged subtotals - check if there's space for all three lines
      const subtotalsSpace = 50; // Space for 3 subtotal lines
      if (currentY + subtotalsSpace > maxUsableHeight) {
        pdf?.addPage();
        currentY = 30;
      }
      
      pdf?.setFontSize(12);
      pdf?.text(`Subtotale prodotti (base): €${flaggedTotal?.toFixed(2)}`, 20, currentY);
      currentY += 12;
      pdf?.text(`IVA 22%: €${(flaggedTotalWithTax - flaggedTotal)?.toFixed(2)}`, 20, currentY);
      currentY += 12;
      pdf?.text(`Subtotale prodotti (con IVA): €${flaggedTotalWithTax?.toFixed(2)}`, 20, currentY);
      currentY += 20;
    }

    // ✅ FINAL TOTALS - ensure they have space and are visible
    const finalTotalsSpace = 80;
    if (currentY + finalTotalsSpace > maxUsableHeight) {
      pdf?.addPage();
      currentY = 30;
    }

    // Enhanced final totals display
    pdf?.setFontSize(14);
    pdf?.setFont(undefined, 'bold');
    
    // Background box for totals
    const finalTotalsBoxHeight = 45;
    pdf?.setFillColor(240, 248, 255);
    pdf?.setDrawColor(41, 128, 185);
    pdf?.setLineWidth(0.5);
    pdf?.rect(15, currentY - 5, 180, finalTotalsBoxHeight, 'FD');
    
    // Reset text properties
    pdf?.setTextColor(0, 0, 0);
    pdf?.setFontSize(13);
    pdf?.setFont(undefined, 'bold');
    
    // Total items
    pdf?.text(`TOTALE ARTICOLI: ${totalItems}`, 20, currentY + 8);
    
    // Final amount - most prominent
    pdf?.setFontSize(16);
    pdf?.setTextColor(0, 120, 0);
    pdf?.setFont(undefined, 'bold');
    pdf?.text(`IMPORTO TOTALE: €${totalAmount?.toFixed(2)}`, 20, currentY + 25);
    
    // Confirmation line
    pdf?.setFontSize(9);
    pdf?.setTextColor(100, 100, 100);
    pdf?.setFont(undefined, 'normal');
    currentY += finalTotalsBoxHeight + 8;
    pdf?.text(`Generato il ${currentDate} - €${totalAmount?.toFixed(2)}`, 20, currentY);

    return pdf;
  };

  // ✅ NEW: Generate TXT content with TAB separation between SKU and quantity
  const generateOrderTXT = () => {
    if (!orderedItems || orderedItems?.length === 0) {
      return '';
    }

    // Create text format: SKU[TAB]QUANTITY (one per line, no headers)
    const txtLines = orderedItems?.map(item => {
      return `${item?.sku}\t${item?.quantity}`;  // Using TAB character instead of space
    });

    return txtLines?.join('\n');
  };

  // ✅ Enhanced WhatsApp order with order tracking to database
  const handleSendWhatsAppOrder = async () => {
    if (orderedItems?.length === 0) {
      showToast?.('Nessun articolo selezionato per l\'ordine', 'warning');
      return;
    }

    setIsUploadingPDF(true);
    
    try {
      // Create PDF with separated sections
      const pdf = generateSeparatedPDF();
      const pdfBlob = pdf?.output('blob');

      // Create TXT content with TAB separation
      const txtContent = generateOrderTXT();

      // Base filename for both files
      const baseFilename = 'ordine';
      
      showToast?.('Preparazione ordine e salvataggio metadati locali...', 'info');
      
      const uploadResult = await storageService?.uploadOrderFiles(pdfBlob, txtContent, baseFilename);
      
      if (!uploadResult?.success) {
        throw new Error(uploadResult?.error || 'Errore durante l\'upload dei file');
      }

      try {
        const orderData = {
          products: orderedItems?.map(item => ({
            sku: item?.sku,
            name: item?.name,
            quantity: parseInt(item?.quantity),
            price: parseFloat(item?.resellerPrice || item?.price || 0),
            isFlagged: item?.isFlagged || false
          })),
          total: totalAmount,
          itemCount: totalItems,
          orderDate: new Date()?.toISOString()?.split('T')?.[0]
        };

        await statisticsService?.addOrder(orderData, 'completed');
        console.log('Order saved to local statistics tracking');
      } catch (dbError) {
        console.log('Order completed but local statistics save failed:', dbError);
      }

      const shareBaseUrl = window.location.origin.includes('localhost')
        ? 'http://localhost:3004'
        : window.location.origin;
      const pdfUrl = `${shareBaseUrl}/shared-file/${uploadResult?.pdf?.id}`;
      const txtUrl = `${shareBaseUrl}/shared-file/${uploadResult?.txt?.id}`;
      const taxInfo = flaggedItems?.length > 0
        ? `\nIVA 22%: €${(flaggedTotalWithTax - flaggedTotal)?.toFixed(2)}`
        : '';

      const whatsappMessage = encodeURIComponent(
        `Ciao ti invio il mio ordine.\n\n` +
        `Totale articoli: ${totalItems}\n` +
        `Importo totale: €${totalAmount?.toFixed(2)}${taxInfo}\n` +
        `Data: ${new Date()?.toLocaleDateString('it-IT')}\n\n` +
        `📋 Ho salvato i file ordine in locale:\n` +
        `• PDF: ${pdfUrl}\n` +
        `• TXT: ${txtUrl}\n\n` +
        `Apri i link e inoltra i file insieme a questo messaggio.`
      );

      window.open(`https://wa.me/393381194891?text=${whatsappMessage}`, '_blank');
      
      showToast?.('✅ Ordine registrato e messaggio WhatsApp generato con link separati ai file.', 'success');
      
    } catch (error) {
      console.error('Errore nella gestione dell\'ordine WhatsApp:', error);
      showToast?.(
        'Errore nel salvataggio dei file locali: ' + error?.message,
        'error'
      );
    } finally {
      setIsUploadingPDF(false);
    }
  };

  // ✅ FIXED: Modified print to only open PDF without automatic print dialog
  const handlePrintPDF = () => {
    if (orderedItems?.length === 0) {
      showToast?.('Nessun articolo da stampare', 'warning');
      return;
    }

    try {
      // Create PDF with separated sections
      const pdf = generateSeparatedPDF();

      try {
        const orderData = {
          products: orderedItems?.map(item => ({
            sku: item?.sku,
            name: item?.name,
            quantity: parseInt(item?.quantity),
            price: parseFloat(item?.resellerPrice || item?.price || 0),
            isFlagged: item?.isFlagged || false
          })),
          total: totalAmount,
          itemCount: totalItems,
          orderDate: new Date()?.toISOString()?.split('T')?.[0]
        };

        statisticsService?.addPrintedOrder(orderData);
        console.log('Printed order saved locally (not counted in statistics)');
      } catch (dbError) {
        console.log('Print completed but local statistics save failed:', dbError);
      }

      // ✅ FIXED: Only open PDF without automatic print dialog
      const pdfBlob = pdf?.output('blob');
      const pdfUrl = URL.createObjectURL(pdfBlob);
      
      // Open PDF in new window for manual printing
      const printWindow = window.open(pdfUrl, '_blank', 'width=800,height=600');
      
      if (printWindow) {
        // Clean up the blob URL after a delay (but don't auto-print)
        setTimeout(() => {
          try {
            URL.revokeObjectURL(pdfUrl);
          } catch (cleanupError) {
            console.log('PDF cleanup completed');
          }
        }, 30000); // 30 seconds delay for cleanup
        
        showToast?.('✅ PDF aperto in nuova finestra. Usa Ctrl+P per stampare quando necessario.', 'success');
        
      } else {
        // Pop-up blocked - create download link instead
        const downloadLink = document.createElement('a');
        downloadLink.href = pdfUrl;
        downloadLink.download = `ordine_${new Date()?.toISOString()?.split('T')?.[0]}.pdf`;
        downloadLink.style.display = 'none';
        document.body?.appendChild(downloadLink);
        downloadLink?.click();
        document.body?.removeChild(downloadLink);
        URL.revokeObjectURL(pdfUrl);
        
        showToast?.('Pop-up bloccato. PDF scaricato automaticamente.', 'warning');
      }
      
    } catch (error) {
      console.error('Errore nella generazione del PDF:', error);
      showToast?.('Errore nella generazione del PDF per la stampa: ' + error?.message, 'error');
    }
  };

  if (orderedItems?.length === 0) {
    return (
      <div className="bg-card rounded-lg border border-border p-6 shadow-subtle">
        <div className="text-center py-8">
          <Icon name="ShoppingCart" size={48} className="text-muted-foreground mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-foreground mb-2">Nessun Articolo Selezionato</h3>
          <p className="text-sm text-muted-foreground">
            Seleziona le quantità dei prodotti desiderati per creare il tuo ordine
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card rounded-lg border border-border p-4 sm:p-6 shadow-subtle overflow-hidden">
      <div className="flex flex-col gap-2 mb-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center space-x-2">
          <Icon name="ShoppingCart" size={20} className="text-primary" />
          <h3 className="text-lg font-semibold text-foreground">Carrello</h3>
        </div>
        <div className="flex items-center space-x-2 text-sm text-muted-foreground">
          <Icon name="Package" size={16} />
          <span>{totalItems} articoli</span>
        </div>
      </div>
      
      {/* ✅ Enhanced Total Amount Display with tax breakdown */}
      {totalAmount > 0 && (
        <div className="bg-primary/5 border border-primary/20 rounded-lg p-3 mb-4">
          <div className="space-y-1">
            {nonFlaggedTotal > 0 && (
              <div className="flex items-center justify-between text-sm">
                <span className="text-foreground">Prodotti standard:</span>
                <span className="font-medium text-green-600">€{nonFlaggedTotal?.toFixed(2)}</span>
              </div>
            )}
            {flaggedItems?.length > 0 && (
              <>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-foreground">Prodotti + IVA 22%:</span>
                  <span className="font-medium text-orange-600">€{flaggedTotalWithTax?.toFixed(2)}</span>
                </div>
                <div className="flex items-center justify-between text-xs text-muted-foreground">
                  <span className="ml-2">• IVA inclusa:</span>
                  <span>€{(flaggedTotalWithTax - flaggedTotal)?.toFixed(2)}</span>
                </div>
              </>
            )}
            <div className="border-t border-border pt-2 mt-2">
              <div className="flex items-center justify-between">
                <span className="text-sm font-medium text-foreground">Totale Finale:</span>
                <span className="text-lg font-bold text-green-600">€{totalAmount?.toFixed(2)}</span>
              </div>
            </div>
          </div>
        </div>
      )}
      
      {/* Cart Items */}
      <div className="space-y-3 mb-4 max-h-64 overflow-y-auto">
        {orderedItems?.map((item) => (
          <div key={item?.sku} className={`${item?.isFlagged ? 'bg-orange-50 border-l-2 border-orange-500' : 'bg-muted/30 border-l-2 border-green-600'} rounded-lg p-3`}>
            <div className="flex items-start justify-between gap-2 mb-2">
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-2 mb-1">
                  <input
                    type="checkbox"
                    checked={item?.isFlagged}
                    onChange={(e) => handleCartFlagChange(item?.sku, e?.target?.checked)}
                    className="w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary focus:ring-2 cursor-pointer flex-shrink-0"
                    title="Contrassegna questo prodotto per IVA 22%"
                  />
                  <h4 className="text-sm font-medium text-foreground truncate">{item?.name}</h4>
                  {item?.isFlagged && (
                    <span className="shrink-0 text-xs bg-orange-200 text-orange-800 px-2 py-0.5 rounded-full">
                      +IVA 22%
                    </span>
                  )}
                </div>
                <p className="text-xs text-muted-foreground">SKU: {item?.sku}</p>
                {item?.resellerPrice && parseFloat(item?.resellerPrice) > 0 && (
                  <div className="text-xs space-y-1">
                    <p className="font-medium text-green-600">
                      €{item?.resellerPrice} per unità
                    </p>
                    {item?.isFlagged && (
                      <p className="font-medium text-orange-600">
                        €{(parseFloat(item?.resellerPrice) * 1.22)?.toFixed(2)} per unità (con IVA)
                      </p>
                    )}
                  </div>
                )}
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleRemoveItem(item?.sku)}
                className="ml-2 w-8 h-8 p-0 text-red-600 hover:bg-red-50 hover:border-red-200"
                title="Rimuovi dal carrello"
              >
                <Icon name="Trash2" size={12} />
              </Button>
            </div>
            
            {/* Quantity controls */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div className="flex items-center space-x-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCartQuantityChange(item?.sku, item?.quantity - 1)}
                  disabled={item?.quantity <= 1}
                  className="w-7 h-7 p-0"
                >
                  <Icon name="Minus" size={10} />
                </Button>
                <Input
                  type="number"
                  min="1"
                  max="999"
                  value={item?.quantity}
                  onChange={(e) => handleCartQuantityChange(item?.sku, e?.target?.value)}
                  className="w-16 text-center text-sm"
                  size="sm"
                />
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCartQuantityChange(item?.sku, item?.quantity + 1)}
                  className="w-7 h-7 p-0"
                >
                  <Icon name="Plus" size={10} />
                </Button>
              </div>
              
              {/* ✅ Enhanced line total with tax display */}
              <div className="self-end text-sm font-semibold text-foreground sm:self-auto">
                {item?.isFlagged ? (
                  <div className="text-right">
                    <div className="text-orange-600">
                      €{(parseFloat(item?.resellerPrice || item?.price || 0) * parseInt(item?.quantity) * 1.22)?.toFixed(2)}
                    </div>
                    <div className="text-xs text-muted-foreground line-through">
                      €{(parseFloat(item?.resellerPrice || item?.price || 0) * parseInt(item?.quantity))?.toFixed(2)}
                    </div>
                  </div>
                ) : (
                  <span>€{(parseFloat(item?.resellerPrice || item?.price || 0) * parseInt(item?.quantity))?.toFixed(2)}</span>
                )}
              </div>
            </div>
            
            {item?.category && (
              <span className="inline-block text-xs bg-secondary/50 text-secondary-foreground px-2 py-0.5 rounded-full mt-2">
                {item?.category}
              </span>
            )}
          </div>
        ))}
      </div>
      
      {/* Action Buttons */}
      <div className="space-y-2">
        {/* ✅ Enhanced WhatsApp Button with dual file upload info */}
        <Button
          variant="default"
          onClick={handleSendWhatsAppOrder}
          iconName={isUploadingPDF ? "Loader2" : "MessageCircle"}
          iconPosition="left"
          iconSize={16}
          fullWidth
          disabled={isUploadingPDF}
          className={`${isUploadingPDF ? 'bg-blue-600 hover:bg-blue-700' : 'bg-green-600 hover:bg-green-700'} text-white ${isUploadingPDF ? 'animate-pulse' : ''}`}
          title="Carica PDF completo + file TXT con lista prodotti per WhatsApp"
        >
          {isUploadingPDF ? 'Caricamento PDF + TXT...' : 'Invia Ordine su WhatsApp'}
        </Button>

        {/* ✅ Updated cleanup info badge with dual file info */}
        {!isUploadingPDF && (
          <div className="text-xs text-muted-foreground text-center bg-muted/30 rounded p-2">
            <Icon name="Clock" size={12} className="inline mr-1" />
            PDF e TXT eliminati automaticamente dopo 30 giorni
          </div>
        )}

        {/* Print Button */}
        <div className="grid grid-cols-1 gap-2">
          <Button
            variant="outline"
            onClick={handlePrintPDF}
            iconName="Printer"
            iconPosition="left"
            iconSize={14}
            className="text-sm"
            fullWidth
            disabled={isUploadingPDF}
          >
            Stampa
          </Button>
        </div>
      </div>
    </div>
  );
};

export default OrderSummary;
